/**
 * @fileoverview Memoized Room Node Component for Floor Plan Canvas
 * @author Hospital Room Scheduler Team
 * @created 16-10-2025 13:43:52
 * 
 * Optimized room rendering component using React.memo to prevent
 * unnecessary re-renders when room data hasn't changed.
 * Implements Konva performance best practices from official documentation.
 */

import React, { useRef, useEffect } from 'react';
import { Group, Rect, Text } from 'react-konva';
import type Konva from 'konva';

// =============================================================================
// TYPES & INTERFACES
// =============================================================================

interface RoomNodeProps {
  room: {
    id: string;
    number: string;
    name: string;
    position: {
      x: number;
      y: number;
      width: number;
      height: number;
    };
    assignments: any[];
    blocks: any[];
    conflicts?: any[];
  };
  color: string;
  opacity: number;
  isSelected: boolean;
  onClick: (room: any) => void;
  onContextMenu: (e: any, room: any) => void;
  onMouseEnter: (e: any, room: any) => void;
  onMouseLeave: () => void;
  onMouseMove: (e: any) => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Memoized Room Node Component
 * 
 * Renders a single room on the floor plan canvas with caching optimization.
 * Uses React.memo with custom comparison to prevent unnecessary re-renders.
 * Implements shape caching as per Konva performance documentation.
 * 
 * Performance optimizations:
 * - React.memo with custom comparison function
 * - Shape caching via Konva's .cache() API
 * - perfectDrawEnabled=false for sub-pixel optimization
 * 
 * @param props - Component props
 * @returns React component
 */
export const RoomNode = React.memo<RoomNodeProps>(({
  room,
  color,
  opacity,
  isSelected,
  onClick,
  onContextMenu,
  onMouseEnter,
  onMouseLeave,
  onMouseMove
}) => {
  const groupRef = useRef<Konva.Group>(null);
  const rectRef = useRef<Konva.Rect>(null);

  /**
   * Cache shape for performance (Konva best practice)
   * From official docs: "Cache node to improve drawing performance"
   */
  useEffect(() => {
    if (rectRef.current && !rectRef.current.isCached()) {
      rectRef.current.cache();
    }
  }, []);

  /**
   * Clear and re-cache when room properties change
   */
  useEffect(() => {
    if (rectRef.current && rectRef.current.isCached()) {
      rectRef.current.clearCache();
      rectRef.current.cache();
    }
  }, [color, room.position.width, room.position.height]);

  /**
   * Determine border styling based on state
   */
  const getBorderStyle = () => {
    if (isSelected) {
      return {
        stroke: '#2563EB',
        strokeWidth: 3,
        shadowColor: '#2563EB',
        shadowBlur: 10,
        shadowOpacity: 0.3
      };
    }
    
    if (room.conflicts && room.conflicts.length > 0) {
      return {
        stroke: '#DC2626',
        strokeWidth: 2,
        shadowColor: '#DC2626',
        shadowBlur: 6,
        shadowOpacity: 0.2
      };
    }
    
    return {
      stroke: '#CBD5E1',
      strokeWidth: 1,
      shadowColor: undefined,
      shadowBlur: 0,
      shadowOpacity: 0
    };
  };

  const borderStyle = getBorderStyle();

  return (
    <Group
      ref={groupRef}
      x={room.position.x}
      y={room.position.y}
      onClick={() => onClick(room)}
      onContextMenu={(e) => onContextMenu(e, room)}
      onMouseEnter={(e) => onMouseEnter(e, room)}
      onMouseLeave={onMouseLeave}
      onMouseMove={onMouseMove}
      perfectDrawEnabled={false}
    >
      {/* Room Rectangle */}
      <Rect
        ref={rectRef}
        width={room.position.width}
        height={room.position.height}
        fill={color}
        opacity={opacity}
        cornerRadius={8}
        {...borderStyle}
      />

      {/* Room Number */}
      <Text
        x={0}
        y={room.position.height / 2 - 14}
        width={room.position.width}
        text={room.number}
        fontSize={14}
        fontStyle="bold"
        fill="#1E293B"
        align="center"
        verticalAlign="middle"
        listening={false}
      />

      {/* Room Name */}
      <Text
        x={0}
        y={room.position.height / 2 + 2}
        width={room.position.width}
        text={room.name}
        fontSize={11}
        fill="#475569"
        align="center"
        verticalAlign="middle"
        ellipsis={true}
        listening={false}
      />

      {/* Assignment Count Badge (if has assignments) */}
      {room.assignments.length > 0 && (
        <>
          <Rect
            x={room.position.width - 30}
            y={8}
            width={24}
            height={18}
            fill="#3B82F6"
            cornerRadius={9}
            listening={false}
          />
          <Text
            x={room.position.width - 30}
            y={8}
            width={24}
            height={18}
            text={room.assignments.length.toString()}
            fontSize={11}
            fontStyle="bold"
            fill="#FFFFFF"
            align="center"
            verticalAlign="middle"
            listening={false}
          />
        </>
      )}

      {/* Block Indicator (if room is blocked) */}
      {room.blocks.length > 0 && (
        <>
          <Rect
            x={8}
            y={8}
            width={20}
            height={20}
            fill="#EF4444"
            cornerRadius={10}
            listening={false}
          />
          <Text
            x={8}
            y={8}
            width={20}
            height={20}
            text="!"
            fontSize={14}
            fontStyle="bold"
            fill="#FFFFFF"
            align="center"
            verticalAlign="middle"
            listening={false}
          />
        </>
      )}
    </Group>
  );
}, (prevProps, nextProps) => {
  /**
   * Custom comparison function for React.memo
   * Only re-render if these specific properties changed
   * This prevents unnecessary re-renders from parent updates
   */
  return (
    prevProps.room.id === nextProps.room.id &&
    prevProps.room.number === nextProps.room.number &&
    prevProps.room.name === nextProps.room.name &&
    prevProps.room.position.x === nextProps.room.position.x &&
    prevProps.room.position.y === nextProps.room.position.y &&
    prevProps.room.position.width === nextProps.room.position.width &&
    prevProps.room.position.height === nextProps.room.position.height &&
    prevProps.room.assignments.length === nextProps.room.assignments.length &&
    prevProps.room.blocks.length === nextProps.room.blocks.length &&
    prevProps.color === nextProps.color &&
    prevProps.opacity === nextProps.opacity &&
    prevProps.isSelected === nextProps.isSelected
  );
});

RoomNode.displayName = 'RoomNode';

export default RoomNode;

